<script setup lang="ts">
import SimpleMenu from "../inputs/SearchRenderers/SimpleMenu.vue";
import MList from "../MList";
import MItemLabel from "../MItemLabel";
import { computed, getCurrentInstance, PropType, ref, useAttrs } from "vue";
import { DrawerUrlOptions } from "../../../store/drawers";
import { usePermission } from "../../../composables/permission";
import { listen } from "../../../utils/bus";
import { useRouter } from "vue-router";
import { generateUUIDv7 } from "../../../utils/helpers";
import { useOnOutsidePress } from "vue-composable";
import MItem from "../MItem";

const props = defineProps({
  border: Boolean,
  click: Function,
  color: String,
  disable: Boolean,
  disabled: Boolean,
  ghost: Boolean,
  icon: String,
  iconOnly: Boolean,
  label: String,
  loading: Boolean,
  menu: Boolean,
  outline: Boolean,
  split: Boolean,
  type: String,
  options: [Array, Object] as PropType<
    Array<{ label: string }> | Record<string, { label: string }>
  >,
  toSlideover: [String, Object, Boolean] as unknown as () => string | DrawerUrlOptions | false,
  toModal: [String, Object, Boolean] as unknown as () => string | DrawerUrlOptions | false,
});

// ENG-20856 Keep class/style on the root wrapper but forward the rest of the
// consumer attrs (e.g. data-testid) onto the clickable trigger — the old
// q-btn-dropdown root was itself the button, so attrs landed on a clickable element.
defineOptions({ inheritAttrs: false });
const attrs = useAttrs();
const rootAttrs = computed(() => ({ class: attrs.class, style: attrs.style }));
const controlAttrs = computed(() => {
  const { class: _class, style: _style, ...rest } = attrs;
  return rest;
});

const { disabled, onClick } = usePermission(props, true, useRouter());
const { proxy } = getCurrentInstance()!;
const targetId = generateUUIDv7();

listen("close-menus", (data: any) => {
  if (data.target === targetId || data === true) isOpened.value = false;
});

const items = computed(() => {
  const options = props.options;
  if (!options) return [];
  if (Array.isArray(options)) {
    return [{ name: "", items: options }];
  }
  return Object.keys(options).map((x) => ({
    name: x,
    items: (options as Record<string, { label: string }[]>)[x],
  }));
});

const isFocus = ref(false);
const isOpened = ref(false);
const focusEl = ref<HTMLElement | null>(null);

function handleBlur() {
  isFocus.value = false;
}

function toggleDropdown() {
  isOpened.value = !isOpened.value;
}

function closeDropdown() {
  isOpened.value = false;
}

function handleTriggerClick(ev: MouseEvent) {
  toggleDropdown();
  onClick(ev);
}

// In split mode the main button normally only fires its primary action. When there is no
// primary action wired (no click/toModal/toSlideover and not a submit button), let the main
// button open the menu too so the whole control is clickable.
const mainHasPrimaryAction = computed(
  () => !!props.click || !!props.toModal || !!props.toSlideover || props.type === "submit",
);

function handleMainClick(ev: MouseEvent) {
  if (mainHasPrimaryAction.value) {
    onClick(ev);
  } else {
    handleTriggerClick(ev);
  }
}

useOnOutsidePress(
  // proxy.$el typed loosely; vue-composable's Ref type differs across the duplicated vue copies
  computed(() => proxy?.$el) as any,
  () => {
    if (isOpened.value) {
      closeDropdown();
      handleBlur();
    }
  },
);

const dropdownIcon = computed(() => {
  if (props.icon) return props.icon;
  if (props.menu) return "fa-solid fa-ellipsis-v";
  // solid caret matches the replaced q-btn-dropdown and the sibling MDropdownAction;
  // flip it to point up while the menu is open
  return isOpened.value ? "fa-solid fa-caret-up" : "fa-solid fa-caret-down";
});

const toggleAriaLabel = computed(() =>
  props.split
    ? `${isOpened.value ? "Close" : "Open"} more options`
    : `${isOpened.value ? "Close" : "Open"} ${props.label}`,
);
</script>
<template>
  <div
    class="m-dropdown-button"
    :class="[
      { disabled: disabled },
      color || type,
      {
        'menu-style': menu,
        ghost: ghost || menu,
        raised: !border && !ghost && !menu,
        outline,
        split,
      },
    ]"
    v-bind="rootAttrs"
  >
    <template v-if="split">
      <button
        class="m-dropdown-button__main medicus-outline"
        :type="type === 'submit' ? 'submit' : 'button'"
        :disabled="disable || disabled || undefined"
        v-bind="controlAttrs"
        @click="handleMainClick"
      >
        <template v-if="$slots.label || iconOnly">
          <slot name="label">
            <span class="sr-only">{{ label }}</span>
          </slot>
        </template>
        <span v-else>{{ label }}</span>
        <i v-if="loading" class="fa-solid fa-spinner fa-spin" aria-hidden="true" />
      </button>
      <button
        class="m-dropdown-button__toggle medicus-outline"
        type="button"
        :disabled="disable || disabled || undefined"
        :aria-expanded="isOpened"
        :aria-label="toggleAriaLabel"
        @click="toggleDropdown"
        @blur="handleBlur"
      >
        <i :class="dropdownIcon" aria-hidden="true" />
      </button>
    </template>

    <button
      v-else
      ref="focusEl"
      class="m-dropdown-button__trigger medicus-outline"
      type="button"
      :disabled="disable || disabled || loading || undefined"
      :aria-expanded="isOpened"
      :aria-label="toggleAriaLabel"
      v-bind="controlAttrs"
      @click="handleTriggerClick"
      @blur="handleBlur"
    >
      <template v-if="$slots.label || iconOnly">
        <slot name="label">
          <span class="sr-only">{{ label }}</span>
        </slot>
      </template>
      <span v-else>{{ label }}</span>
      <i v-if="!loading" :class="dropdownIcon" aria-hidden="true" />
      <i v-else class="fa-solid fa-spinner fa-spin" aria-hidden="true" />
    </button>

    <SimpleMenu :id="targetId" no-auto-open :model-value="isOpened" :block-dispatch="true">
      <div ref="focusEl" class="m-dropdown-button-menu" @click="closeDropdown">
        <slot v-bind="{ loading }">
          <m-list :class="{ 'grouped-list': !Array.isArray(options) }">
            <template v-for="(group, index) in items" :key="`option-${index}`">
              <div v-if="group.name" class="dropdown-button-list-title">
                {{ group.name }}
              </div>
              <m-item
                v-for="(option, i) in group.items"
                :key="`option-item-${group.name}-${i}`"
                v-bind="option"
                clickable
                role="menuitem"
              >
                <m-item-section>
                  <m-item-label>{{ option.label }}</m-item-label>
                </m-item-section>
              </m-item>
            </template>
          </m-list>
        </slot>
      </div>
    </SimpleMenu>
  </div>
</template>

<style lang="scss">
.m-dropdown-button {
  font-style: normal;
  font-weight: bold;
  font-size: 14px;
  line-height: 150%;
  height: 35px;
  text-align: center;
  border-radius: 4px;
  border: 1px solid var(--theme-accent-blue);
  background: var(--theme-blue);
  color: var(--white);
  cursor: pointer;
  outline: none;

  display: inline-flex;
  align-items: stretch;

  .m-dropdown-button__trigger,
  .m-dropdown-button__main,
  .m-dropdown-button__toggle {
    background: transparent;
    border: none;
    color: inherit;
    font: inherit;
    font-weight: bold;
    cursor: pointer;
    display: flex;
    align-items: center;
    outline: none;

    &:disabled {
      cursor: default;
    }
  }

  .m-dropdown-button__trigger {
    flex: 1;
    padding: 2px 9px 2px 12px;
    // gap drives the label↔icon spacing; no per-icon margin (which also padded the
    // caret's right side, making it sit further from the edge than q-btn-dropdown did)
    gap: var(--gap-2);
  }

  &.split {
    padding: 0;
    border: none;

    .m-dropdown-button__main {
      padding: 3px 12px;
      border-right: 1px solid;
      flex: 1;
    }

    .m-dropdown-button__toggle {
      padding: 3px 0;
      border-left: 1px solid;
      min-width: 40px;
      justify-content: center;
    }
  }

  &.secondary,
  &[type="secondary"] {
    font-weight: bold;
    background: var(--white);
    color: var(--text-color);
    border-color: var(--text-color-light);

    &:hover {
      background: #e7eaee;
    }
  }

  &:hover {
    background: var(--theme-accent-blue);
  }

  &.raised:not([type="danger"]):not(.secondary) {
    color: var(--white);
    background: var(--theme-blue);
    border: 1px solid var(--theme-accent-blue);

    &:hover {
      background: var(--theme-accent-blue);
      border: 1px solid var(--theme-accent-blue);
    }
  }

  &.outline {
    color: #90a0b7;
    border: 1px solid var(--grey);

    &:hover {
      background: var(--grey);
    }
  }

  &.ghost {
    border: none;
    box-shadow: none;
    color: var(--theme-blue);
    background: transparent;
    font-weight: bold;
    border: 2px solid transparent;

    .m-dropdown-button__trigger {
      //padding-right: 0;
    }

    .m-dropdown-button__trigger > span {
      text-decoration-line: underline;
      text-underline-offset: 3px;

      &:hover:not(.disabled),
      &:focus-within {
        color: var(--theme-darker-blue);
        text-decoration-width: 3px;
        text-decoration-thickness: 3px;
      }
    }

    i {
      text-decoration: none !important;
      text-decoration-line: none !important;
    }

    &:hover {
      background: transparent;
      color: #003078;

      &[type="secondary"] {
        color: var(--text-color);
      }
    }

    &:focus {
      border-radius: 4px;
      border-color: var(--theme-blue);
      outline-color: var(--theme-blue);

      &[type="secondary"] {
        color: var(--grey);
      }
    }
  }

  &.menu-style {
    padding: 0;
    color: var(--text-color);
  }

  &.disabled {
    font-weight: bold;
    background: var(--white) !important;
    color: var(--grey) !important;

    &.split {
      border: 1px solid var(--grey-light) !important;

      .m-dropdown-button__toggle {
        pointer-events: none;
      }

      .m-dropdown-button__main {
        border-right: none !important;
      }
    }

    &:hover {
      background: var(--white) !important;
      border-color: var(--grey-light) !important;
      color: var(--grey) !important;
    }
  }
}

.m-dropdown-button-menu {
  max-height: 90vh;
  // reset the trigger's centred text-align so menu items default to left-aligned
  text-align: left;

  .m-list {
    & > a {
      min-height: 40px;
    }

    &.grouped-list {
      .dropdown-button-list-title {
        min-height: 40px !important;

        font-family: Arial, sans-serif;
        font-style: normal;
        font-weight: 400;
        font-size: 14px;
        line-height: 100%;

        color: var(--grey-darkest);

        padding-left: 1rem;

        display: flex;
        align-items: center;
      }

      .m-item {
        min-height: 30px !important;
        border-radius: 4px;

        font-size: 14px;
        padding: 6px 16px;
      }
    }
  }
}
</style>
